// AI Pipeline Service API Client
// Direct API calls to ai-pipeline-service (port 3007)

import { apiClient } from './api-client';
import type {
  GeneratePipelineRequest,
  GeneratePipelineResponse,
  EstimateTokensRequest,
  EstimateTokensResponse,
  AIConfigResponse,
  ChatRequest,
  ChatGenerateRequest,
  PipelineChatMessageRequest,
  PipelineChatMessageResponse,
  ApplyProposalRequest,
  ApplyProposalResponse,
  RejectProposalResponse,
  SSETokenEvent,
  SSESuggestionEvent,
  SSEDoneEvent,
  SSEErrorEvent,
} from '@/types/ai-pipeline';

const AI_PIPELINE_BASE = import.meta.env.VITE_AI_PIPELINE_URL || '';

function isHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

function buildBuilderChatUrl(path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  if (AI_PIPELINE_BASE) {
    if (isHttpUrl(AI_PIPELINE_BASE)) {
      return `${AI_PIPELINE_BASE}/api/v1${normalizedPath}`;
    }

    // Proxy-style base (e.g. "/ai-api")
    return `${AI_PIPELINE_BASE}${normalizedPath}`;
  }

  const proxyPath = `/ai-api${normalizedPath}`;
  if (typeof window !== 'undefined' && window.location?.origin) {
    // Absolute URL prevents axios baseURL "/api/v1" from prefixing this request.
    return `${window.location.origin}${proxyPath}`;
  }

  return proxyPath;
}

/**
 * Generate a pipeline configuration using AI
 *
 * POST /api/v1/pipelines/generate
 */
export async function generateAIPipeline(
  request: GeneratePipelineRequest
): Promise<GeneratePipelineResponse> {
  const url = AI_PIPELINE_BASE
    ? `${AI_PIPELINE_BASE}/api/v1/pipelines/generate`
    : '/api/v1/pipelines/generate';

  return apiClient.post<GeneratePipelineResponse>(url, request);
}

/**
 * Estimate token usage for a generation request
 *
 * POST /api/v1/pipelines/estimate
 */
export async function estimateTokens(
  request: EstimateTokensRequest
): Promise<EstimateTokensResponse> {
  const url = AI_PIPELINE_BASE
    ? `${AI_PIPELINE_BASE}/api/v1/pipelines/estimate`
    : '/api/v1/pipelines/estimate';

  return apiClient.post<EstimateTokensResponse>(url, request);
}

/**
 * Get current AI configuration (admin only)
 *
 * GET /api/v1/ai/config
 */
export async function getAIConfig(): Promise<AIConfigResponse> {
  const url = AI_PIPELINE_BASE
    ? `${AI_PIPELINE_BASE}/api/v1/ai/config`
    : '/api/v1/ai/config';

  return apiClient.get<AIConfigResponse>(url);
}

/**
 * Send a chat message and receive SSE stream
 * Returns AbortController to cancel the stream
 */
export function streamChat(
  request: ChatRequest,
  callbacks: {
    onToken: (data: SSETokenEvent) => void;
    onSuggestion: (data: SSESuggestionEvent) => void;
    onDone: (data: SSEDoneEvent) => void;
    onError: (data: SSEErrorEvent) => void;
  }
): AbortController {
  const controller = new AbortController();
  const token = localStorage.getItem('accessToken');
  const openaiToken = localStorage.getItem('openai_token');

  const url = AI_PIPELINE_BASE
    ? `${AI_PIPELINE_BASE}/api/v1/pipelines/chat`
    : '/ai-api/pipelines/chat';

  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(openaiToken ? { 'X-OpenAI-Token': openaiToken } : {}),
    },
    body: JSON.stringify(request),
    signal: controller.signal,
  })
    .then(async (response) => {
      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ error: { message: 'Stream failed' } }));
        callbacks.onError({
          message: errorData.error?.message || `HTTP ${response.status}`,
        });
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        callbacks.onError({ message: 'No stream body' });
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let currentEvent = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            const data = line.slice(6);
            try {
              const parsed = JSON.parse(data);
              switch (currentEvent) {
                case 'token':
                  callbacks.onToken(parsed);
                  break;
                case 'suggestion':
                  callbacks.onSuggestion(parsed);
                  break;
                case 'done':
                  callbacks.onDone(parsed);
                  break;
                case 'error':
                  callbacks.onError(parsed);
                  break;
              }
            } catch {
              // Skip unparseable data
            }
            currentEvent = '';
          }
        }
      }
    })
    .catch((error) => {
      if (error.name !== 'AbortError') {
        callbacks.onError({ message: error.message || 'Stream failed' });
      }
    });

  return controller;
}

/**
 * Generate final pipeline from chat conversation
 */
export async function generateFromChat(
  request: ChatGenerateRequest
): Promise<GeneratePipelineResponse> {
  const url = AI_PIPELINE_BASE
    ? `${AI_PIPELINE_BASE}/api/v1/pipelines/chat/generate`
    : '/ai-api/pipelines/chat/generate';

  const token = localStorage.getItem('accessToken');
  const openaiToken = localStorage.getItem('openai_token');
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(openaiToken ? { 'X-OpenAI-Token': openaiToken } : {}),
    },
    body: JSON.stringify(request),
  });

  const data = await response
    .json()
    .catch(() => ({ error: { message: `HTTP ${response.status}` } }));

  if (!response.ok) {
    throw new Error(data.error?.message || `HTTP ${response.status}`);
  }

  return data as GeneratePipelineResponse;
}

/**
 * Send a pipeline-scoped chat message for builder enrichment workflow.
 */
export async function postPipelineChatMessage(
  pipelineId: string,
  request: PipelineChatMessageRequest,
): Promise<PipelineChatMessageResponse> {
  const url = buildBuilderChatUrl(`/pipelines/${pipelineId}/chat/messages`);

  return apiClient.post<PipelineChatMessageResponse>(url, request);
}

/**
 * Apply approved operations from a proposal.
 */
export async function applyPipelineProposal(
  pipelineId: string,
  proposalId: string,
  request: ApplyProposalRequest,
): Promise<ApplyProposalResponse> {
  const url = buildBuilderChatUrl(
    `/pipelines/${pipelineId}/chat/proposals/${proposalId}/apply`,
  );

  return apiClient.post<ApplyProposalResponse>(url, request);
}

/**
 * Reject an active proposal.
 */
export async function rejectPipelineProposal(
  pipelineId: string,
  proposalId: string,
): Promise<RejectProposalResponse> {
  const url = buildBuilderChatUrl(
    `/pipelines/${pipelineId}/chat/proposals/${proposalId}/reject`,
  );

  return apiClient.post<RejectProposalResponse>(url);
}
